import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Plug, CheckCircle2, XCircle, RefreshCw, Save, Send, Trash2, Info,
  ArrowRight, Zap, Crown, ChevronDown, Settings2, HelpCircle, Mail,
} from "lucide-react";

/**
 * OTA channels displayed on the hero. Kept as data to make future
 * additions/removals trivial and to keep the JSX readable.
 */
const OTA_CHANNELS = [
  { name: "Booking.com", color: "#003580" },
  { name: "Airbnb",      color: "#FF385C" },
  { name: "Expedia",     color: "#FFC72C" },
  { name: "Vrbo",        color: "#245ABC" },
  { name: "TripAdvisor", color: "#00AF87" },
];

export default function ChannelManager() {
  const [providers, setProviders] = useState([]);
  const [status, setStatus] = useState({ connected: false });
  const [rooms, setRooms] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("mock");
  const [credentials, setCredentials] = useState({ api_key: "" });
  const [mappings, setMappings] = useState({});  // room_id -> external_id
  const [pushForm, setPushForm] = useState({ room_id: "", date_from: "", date_to: "", availability: 1, rate_eur: 0 });
  const [busy, setBusy] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: pv }, { data: st }, { data: rm }] = await Promise.all([
        api.get("/channel-manager/providers"),
        api.get("/channel-manager/status"),
        api.get("/rooms"),
      ]);
      setProviders(pv); setStatus(st); setRooms(rm);
      if (st.connected) {
        setAdvancedOpen(true);
        const m = {};
        (st.room_mappings || []).forEach(x => { m[x.room_id] = x.external_id; });
        setMappings(m);
      }
      if (rm[0] && !pushForm.room_id) {
        setPushForm(p => ({ ...p, room_id: rm[0].id }));
      }
    } catch (err) {
      if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || "Errore");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/channel-manager/connect", {
        provider: selectedProvider, credentials,
      });
      setStatus(data);
      toast.success("Channel Manager connesso");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!confirm("Disconnetto il Channel Manager?")) return;
    try {
      await api.delete("/channel-manager/connect");
      setStatus({ connected: false });
      setMappings({});
      toast.success("Disconnesso");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const saveMappings = async () => {
    try {
      const list = Object.entries(mappings)
        .filter(([_, ext]) => ext && ext.trim())
        .map(([room_id, external_id]) => ({ room_id, external_id: external_id.trim() }));
      await api.put("/channel-manager/room-mappings", list);
      toast.success("Mapping salvato");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const push = async (kind) => {
    if (!pushForm.room_id || !pushForm.date_from || !pushForm.date_to) {
      toast.error("Compila camera + date"); return;
    }
    try {
      const { data } = await api.post(`/channel-manager/push/${kind}`, pushForm);
      toast.success(`Push ${kind} inviato${data.status === "mocked" ? " (mock)" : ""}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="p-6 md:p-12 max-w-7xl mx-auto" data-testid="channel-manager-page">
      {/* ─── Hero ───────────────────────────────────────────────── */}
      <p className="text-xs tracked text-blue-700 mb-2">
        SINCRONIZZAZIONE OTA · UN CALENDARIO PER TUTTI I PORTALI
      </p>
      <h1 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight mb-3">
        Le tue prenotazioni, tutte in un posto.
      </h1>
      <p className="text-slate-600 text-lg max-w-2xl mb-6">
        Xenia dialoga con Booking, Airbnb, Expedia, Vrbo e TripAdvisor. Scegli
        la modalità giusta per te — puoi partire gratis oggi.
      </p>

      {/* OTA badges */}
      <div className="flex flex-wrap gap-2 mb-10" data-testid="cm-ota-badges">
        {OTA_CHANNELS.map(o => (
          <span
            key={o.name}
            className="inline-flex items-center gap-2 border border-slate-200 bg-white px-3 py-1.5 text-sm rounded-lg"
          >
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: o.color }}
              aria-hidden
            />
            {o.name}
          </span>
        ))}
      </div>

      {/* ─── Two paths ──────────────────────────────────────────── */}
      <div className="grid md:grid-cols-2 gap-5 mb-10">
        {/* Path A: iCal (free) */}
        <div
          className="border border-slate-200 bg-white p-7 flex flex-col relative"
          data-testid="cm-path-ical"
        >
          <span className="absolute -top-2.5 left-6 text-[10px] tracked bg-green-800 text-white px-2 py-0.5 rounded-lg">
            CONSIGLIATO PER INIZIARE
          </span>
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-green-50 border border-green-200 flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-green-800" />
            </div>
            <div>
              <p className="text-xs tracked text-green-800 mb-0.5">GRATIS · SUBITO</p>
              <h2 className="font-heading font-semibold text-2xl leading-tight">Sync iCal</h2>
            </div>
          </div>
          <p className="text-slate-600 text-sm mb-4">
            Copi il link .ics di ogni portale, Xenia importa le date già
            prenotate ogni ora ed evita doppi booking. Funziona anche con
            AirBnB e Booking senza intermediari.
          </p>

          <ul className="text-sm space-y-2 mb-5 text-slate-700">
            <li className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-700 mt-0.5 flex-shrink-0" />
              Importa date occupate da tutte le OTA
            </li>
            <li className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-700 mt-0.5 flex-shrink-0" />
              Esporta il calendario Xenia verso le OTA
            </li>
            <li className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-700 mt-0.5 flex-shrink-0" />
              Aggiornamento ogni ora, automatico
            </li>
            <li className="flex gap-2">
              <XCircle className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
              <span className="text-slate-500">
                Non aggiorna prezzi (solo disponibilità)
              </span>
            </li>
          </ul>

          <Link
            to="/app/ical-sync"
            data-testid="cm-goto-ical"
            className="mt-auto inline-flex items-center justify-center gap-2 bg-green-800 text-white px-5 py-3 rounded-lg hover:bg-green-700 font-medium"
          >
            Vai alla sincronizzazione iCal <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Path B: Channel Manager (paid) */}
        <div
          className="border border-slate-200 bg-white p-7 flex flex-col relative"
          data-testid="cm-path-pro"
        >
          <span className="absolute -top-2.5 left-6 text-[10px] tracked bg-blue-700 text-white px-2 py-0.5 rounded-lg">
            PRO · MULTI-STRUTTURA
          </span>
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
              <Crown className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <p className="text-xs tracked text-blue-700 mb-0.5">A PAGAMENTO · TEMPO REALE</p>
              <h2 className="font-heading font-semibold text-2xl leading-tight">Channel Manager professionale</h2>
            </div>
          </div>
          <p className="text-slate-600 text-sm mb-4">
            Per hotel e host multi-appartamento. Xenia parla via API con un
            Channel Manager certificato (NextPax, Channex, Beds24, SiteMinder)
            che invia in tempo reale prezzi, disponibilità e prenotazioni
            complete su tutte le OTA.
          </p>

          <ul className="text-sm space-y-2 mb-5 text-slate-700">
            <li className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
              Aggiorna <strong>prezzi + disponibilità</strong> in tempo reale
            </li>
            <li className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
              Importa <strong>prenotazioni complete</strong> (nome, importo, note)
            </li>
            <li className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
              Uno scudo contro doppi booking, sincronia &lt; 1 minuto
            </li>
            <li className="flex gap-2">
              <Info className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
              <span className="text-slate-500">
                Serve un contratto con un Channel Manager (~20-40€/mese)
              </span>
            </li>
          </ul>

          <a
            href="mailto:sales@xeniaregistry.com?subject=Attivazione%20Channel%20Manager%20Pro"
            data-testid="cm-contact-sales"
            className="mt-auto inline-flex items-center justify-center gap-2 bg-blue-700 text-white px-5 py-3 rounded-lg hover:bg-blue-700 font-medium"
          >
            <Mail className="w-4 h-4" /> Parliamone insieme
          </a>
        </div>
      </div>

      {/* ─── Comparison table ────────────────────────────────────── */}
      <section className="border border-slate-200 bg-white overflow-hidden mb-10">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading font-semibold text-2xl">Confronto veloce</h2>
        </div>
        <table className="w-full text-sm" data-testid="cm-comparison-table">
          <thead className="bg-slate-50 text-xs tracked text-slate-500">
            <tr>
              <th className="text-left px-6 py-3 font-normal">Funzione</th>
              <th className="text-center px-6 py-3 font-normal">Sync iCal</th>
              <th className="text-center px-6 py-3 font-normal bg-blue-50 text-blue-700">
                Channel Manager Pro
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {[
              ["Costo", "Gratis", "20-40 €/mese"],
              ["Blocco doppi booking", "yes", "yes"],
              ["Import date occupate", "yes", "yes"],
              ["Import nome ospite e importo", "no", "yes"],
              ["Aggiornamento prezzi", "no", "yes"],
              ["Velocità sincronia", "~1 ora", "< 1 minuto"],
              ["Adatto a", "1-5 alloggi", "5+ alloggi / hotel"],
            ].map(([feature, ical, pro]) => (
              <tr key={feature}>
                <td className="px-6 py-3 text-slate-700">{feature}</td>
                <td className="px-6 py-3 text-center">
                  <FeatureFlag value={ical} onColor="text-green-700" />
                </td>
                <td className="px-6 py-3 text-center bg-blue-50/40">
                  <FeatureFlag value={pro} onColor="text-blue-700" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ─── How it works (FAQ) ──────────────────────────────────── */}
      <section className="mb-10" data-testid="cm-faq">
        <h2 className="font-heading font-semibold text-2xl mb-4 flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-blue-700" /> Come funziona in pratica
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            {
              n: "1",
              title: "Su Booking / Airbnb / …",
              body: "Nel pannello del portale copi il link del calendario (iCal export). Lo trovi in Impostazioni → Sincronizzazione calendario.",
            },
            {
              n: "2",
              title: "In Xenia",
              body: "Apri Sync iCal, incolli il link, scegli a quale camera abbinarlo. Xenia si sincronizza subito e poi ogni ora.",
            },
            {
              n: "3",
              title: "Dai portali verso Xenia",
              body: "Copi l'URL export di Xenia (uno per camera) e lo incolli nel pannello del portale. Fatto: nessun doppio booking.",
            },
          ].map(s => (
            <div
              key={s.n}
              className="border border-slate-200 bg-white p-5"
            >
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-heading font-semibold font-bold mb-3">
                {s.n}
              </div>
              <h3 className="font-medium text-slate-900 mb-1">{s.title}</h3>
              <p className="text-sm text-slate-600">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Advanced (existing technical UI, collapsed) ─────────── */}
      <section className="border border-slate-200 bg-white mb-8" data-testid="cm-advanced">
        <button
          type="button"
          onClick={() => setAdvancedOpen(v => !v)}
          data-testid="cm-advanced-toggle"
          aria-expanded={advancedOpen}
          className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50"
        >
          <span className="flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-slate-500" />
            <span className="font-medium">Configurazione avanzata</span>
            <span className="text-xs text-slate-500 hidden sm:inline">
              — solo se hai già un Channel Manager professionale
            </span>
            {status.connected && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] tracked bg-green-50 text-green-800 border border-green-200 px-2 py-0.5 rounded-lg">
                <CheckCircle2 className="w-3 h-3" /> CONNESSO
              </span>
            )}
          </span>
          <ChevronDown
            className={`w-5 h-5 text-slate-400 transition-transform ${advancedOpen ? "rotate-180" : ""}`}
          />
        </button>

        {advancedOpen && (
          <div className="border-t border-slate-200 px-6 py-6 space-y-6">
      {/* Connect / Status panel */}
      <section className="border border-slate-200 bg-white p-6" data-testid="cm-connection">
        <div className="flex items-center gap-2 mb-4">
          <Plug className="w-5 h-5 text-blue-700" />
          <h2 className="font-heading font-semibold text-2xl">Connessione</h2>
        </div>

        {status.connected ? (
          <div className="flex flex-wrap items-center gap-4" data-testid="cm-status-connected">
            <span className="inline-flex items-center gap-2 text-green-800 bg-green-50 border border-green-200 px-3 py-1 text-sm rounded-lg">
              <CheckCircle2 className="w-4 h-4" /> {status.provider} · {status.account_label || "—"}
            </span>
            <span className="text-xs text-slate-500">
              connesso il {status.connected_at ? new Date(status.connected_at).toLocaleString("it-IT") : "—"}
            </span>
            <button onClick={disconnect}
              className="ml-auto inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-sm text-red-700 hover:bg-slate-50" data-testid="cm-disconnect">
              <Trash2 className="w-4 h-4" /> Disconnetti
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-3 items-end" data-testid="cm-connect-form">
            <label className="block">
              <span className="text-xs tracked text-slate-500 block mb-1">PROVIDER</span>
              <select value={selectedProvider} onChange={e => setSelectedProvider(e.target.value)}
                className="border border-slate-300 px-3 py-2 bg-white w-full text-sm" data-testid="cm-provider">
                {providers.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.label}{!p.production_ready ? " · dev only" : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="block md:col-span-2">
              <span className="text-xs tracked text-slate-500 block mb-1">API KEY</span>
              <input type="password" value={credentials.api_key || ""}
                onChange={e => setCredentials({ ...credentials, api_key: e.target.value })}
                placeholder='(per il Mock provider, scrivi "demo")'
                className="border border-slate-300 px-3 py-2 bg-white w-full text-sm font-mono"
                data-testid="cm-api-key" />
            </label>
            <button onClick={connect} disabled={busy}
              className="inline-flex items-center justify-center gap-2 bg-stone-900 text-stone-50 px-4 py-2 text-sm rounded-lg hover:bg-stone-800 disabled:opacity-50 md:col-start-3" data-testid="cm-connect">
              {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
              {busy ? "Verifico…" : "Connetti"}
            </button>
            <p className="md:col-span-3 text-xs text-slate-500 inline-flex items-start gap-2 mt-2">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              Oggi è disponibile solo il provider <strong>Mock</strong> per scopi di sviluppo. Per Channex/Beds24/SiteMinder, contatta il supporto Xenia.
            </p>
          </div>
        )}

        {status.connected && status.channels?.length > 0 && (
          <div className="mt-6">
            <h3 className="text-xs tracked text-slate-500 mb-2">CANALI ATTIVI</h3>
            <div className="flex flex-wrap gap-2" data-testid="cm-channels">
              {status.channels.map(c => (
                <span key={c.id} className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg border ${
                  c.status === "active"
                    ? "bg-green-50 text-green-800 border-green-200"
                    : "bg-slate-50 text-slate-500 border-slate-200"
                }`}>
                  {c.status === "active" ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                  {c.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Room mapping */}
      {status.connected && (
        <section className="border border-slate-200 bg-white p-6 mb-8" data-testid="cm-room-mapping">
          <h2 className="font-heading font-semibold text-2xl mb-2">Mapping camere</h2>
          <p className="text-slate-600 text-sm mb-4">
            Per ciascuna camera Xenia, indica l&apos;identificatore corrispondente sul Channel Manager (lo trovi nel pannello del CM).
          </p>
          <div className="space-y-2">
            {rooms.length === 0 ? (
              <p className="text-slate-500 text-sm">Aggiungi prima delle camere.</p>
            ) : rooms.map(r => (
              <div key={r.id} className="grid md:grid-cols-2 gap-3 items-center">
                <span className="text-sm">{r.name} <span className="text-slate-500 text-xs">({r.type})</span></span>
                <input value={mappings[r.id] || ""}
                  onChange={e => setMappings({ ...mappings, [r.id]: e.target.value })}
                  placeholder="external_id sul CM"
                  className="border border-slate-300 px-3 py-2 bg-white text-sm font-mono"
                  data-testid={`cm-map-${r.id}`} />
              </div>
            ))}
          </div>
          <button onClick={saveMappings}
            className="mt-4 inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-4 py-2 text-sm rounded-lg hover:bg-stone-800" data-testid="cm-save-mappings">
            <Save className="w-4 h-4" /> Salva mapping
          </button>
        </section>
      )}

      {/* Push panel */}
      {status.connected && (
        <section className="border border-slate-200 bg-white p-6" data-testid="cm-push">
          <h2 className="font-heading font-semibold text-2xl mb-2">Push manuale</h2>
          <p className="text-slate-600 text-sm mb-4">
            Forza un push immediato di disponibilità o tariffa per testare la connessione. In produzione i push partono in automatico ad ogni modifica di prenotazione/prezzo.
          </p>
          <div className="grid md:grid-cols-5 gap-3 mb-4 items-end">
            <label className="block">
              <span className="text-xs tracked text-slate-500 block mb-1">CAMERA</span>
              <select value={pushForm.room_id} onChange={e => setPushForm({ ...pushForm, room_id: e.target.value })}
                className="border border-slate-300 px-3 py-2 bg-white w-full text-sm" data-testid="cm-push-room">
                {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs tracked text-slate-500 block mb-1">DA</span>
              <input type="date" value={pushForm.date_from}
                onChange={e => setPushForm({ ...pushForm, date_from: e.target.value })}
                className="border border-slate-300 px-3 py-2 bg-white w-full text-sm" data-testid="cm-push-from" />
            </label>
            <label className="block">
              <span className="text-xs tracked text-slate-500 block mb-1">A</span>
              <input type="date" value={pushForm.date_to}
                onChange={e => setPushForm({ ...pushForm, date_to: e.target.value })}
                className="border border-slate-300 px-3 py-2 bg-white w-full text-sm" data-testid="cm-push-to" />
            </label>
            <label className="block">
              <span className="text-xs tracked text-slate-500 block mb-1">DISPONIBILI</span>
              <input type="number" min="0" value={pushForm.availability}
                onChange={e => setPushForm({ ...pushForm, availability: Number(e.target.value) })}
                className="border border-slate-300 px-3 py-2 bg-white w-full text-sm" data-testid="cm-push-avail" />
            </label>
            <label className="block">
              <span className="text-xs tracked text-slate-500 block mb-1">PREZZO €/NOTTE</span>
              <input type="number" min="0" step="0.01" value={pushForm.rate_eur}
                onChange={e => setPushForm({ ...pushForm, rate_eur: Number(e.target.value) })}
                className="border border-slate-300 px-3 py-2 bg-white w-full text-sm" data-testid="cm-push-rate" />
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={() => push("availability")}
              className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50" data-testid="cm-push-availability-btn">
              <Send className="w-4 h-4" /> Push disponibilità
            </button>
            <button onClick={() => push("rate")}
              className="inline-flex items-center gap-2 bg-blue-700 text-white px-4 py-2 text-sm rounded-lg hover:bg-blue-700" data-testid="cm-push-rate-btn">
              <Send className="w-4 h-4" /> Push prezzo
            </button>
          </div>
          {status.last_push_at && (
            <p className="text-xs text-slate-500 mt-3">
              Ultimo push: {new Date(status.last_push_at).toLocaleString("it-IT")}
            </p>
          )}
        </section>
      )}
          </div>
        )}
      </section>
    </div>
  );
}


/**
 * Compact cell renderer for the iCal vs Pro comparison table.
 * Extracted to eliminate nested ternaries in the table body.
 */
function FeatureFlag({ value, onColor }) {
  if (value === "yes") return <CheckCircle2 className={`w-4 h-4 ${onColor} inline`} />;
  if (value === "no")  return <XCircle className="w-4 h-4 text-stone-300 inline" />;
  return <span className="text-slate-700">{value}</span>;
}
